import English from "@uppy/locales/lib/en_US";
import German from "@uppy/locales/lib/de_DE";
import { createI18n } from 'vue-i18n';

const DOMAIN_SETTINGS = {
    'makemymosaic.de': { locale: 'de', country: 'de' },
    'makemymosaic.ch': { locale: 'de', country: 'ch' },
    'mosapics.com': { locale: 'en', country: 'us' },
    'makemymosaic.eu': { locale: 'en', country: 'eu' },
};

export function getDomainSettings(hostname = window.location.hostname) {
    const normalizedHostname = hostname.toLowerCase().replace(/^www\./, '');
    return DOMAIN_SETTINGS[normalizedHostname] || { locale: 'de', country: 'de' };
}

const domainSettings = getDomainSettings();

export const Language = {
    i18n: domainSettings.locale,
    country: domainSettings.country,
    uppy: domainSettings.locale === 'de' ? German : English,
    dashboard: domainSettings.locale === 'de' ? {
        header: {
            browseFiles: 'Bild auswählen',
            dropPasteFiles: '%{browseFiles} oder hierher ziehen'
        },
        tiles: {
            browseFiles: 'Bilder auswählen',
            dropPasteFiles: '%{browseFiles} oder hierher ziehen'
        }
    } : {
        header: {
            browseFiles: 'Select an image',
            dropPasteFiles: '%{browseFiles} or drop it here'
        },
        tiles: {
            browseFiles: 'Select images',
            dropPasteFiles: '%{browseFiles} or drop them here'
        }
    }
};

export default createI18n({
    locale: Language.i18n,
    fallbackLocale: 'en',
    messages: {
        en: {
            design_rotation_failed: 'This photo could not be rotated. Please try again.',
            design_saving: 'Saving…',
            design_eyebrow: 'Your photo mosaic',
            design_free: 'Free & no obligation',
            design_steps: 'Preview request progress',
            design_step_count: 'Step {step} of {total}',
            design_step_1: 'Main image',
            design_step_2: 'Tile images',
            design_step_3: 'Product',
            design_step_4: 'Your details',
            design_step_5: 'Done',
            design_title_1: 'Which picture tells your story?',
            design_title_2: 'Bring your memories together.',
            design_title_3: 'Find the right place for your mosaic.',
            design_title_4: 'A few final details.',
            design_title_5: 'Your memories are in good hands.',
            design_description_1: 'Choose the main image that your smaller photos will come together to create.',
            design_description_2: 'Add the individual photos that will make up your mosaic. Every photo is part of the story.',
            design_description_3: 'Choose a material and size. After the preview you can still change your mind at any time.',
            design_description_4: 'Tell us your wishes. Our design team will create your personal preview.',
            design_description_5: 'Thank you! Our design team will take it from here.',
            design_loading: 'Getting your preview ready…',
            design_privacy: 'Your photos stay private and are deleted after a short time.',
            design_main_tip_title: 'One image. The whole story.',
            design_main_tip: 'A portrait, a landscape or your favourite moment — choose a clear image with a strong subject.',
            design_file_types: 'JPG / JPEG, JFIF, PNG, HEIC / HEIF, WEBP · Up to 50 MB per photo',
            design_next_tiles: 'Add tile images',
            design_next_product: 'Choose your product',
            design_next_contact: 'Final details',
            design_tiles_uploaded: 'photos uploaded',
            design_tiles_recommendation: 'At least 20 · 50+ recommended',
            design_tiles_hint: 'You can add more photos using the + above.',
            design_expand: 'Fullscreen',
            design_material: 'Material',
            design_size: 'Size',
            design_room_preview: 'Preview of your selected image on a wall',
            design_rotate_left: 'Rotate {filename} left',
            design_rotate_right: 'Rotate {filename} right',
            design_remove_named: 'Remove {filename}',
            alert_no_header: 'Please select your main image.',
            next: 'Next',
            back: 'Back',
            close: 'Close',
            select: 'Select',
            step_1_title: '1. Select main image',
            step_2_title: '2. Select tile images',
            step_3_title: '3. Select product',
            step_4_title: '4. Contact',
            step_5_title: '5. Done',
            preview_code: 'Your preview code: {preview_code}',
            add_more_images: 'Add more tile images using the "+" in the upper right corner.',
            fullscreen: 'View tile images fullscreen',
            exit_fullscreen: 'Exit fullscreen',
            rotate_images: 'Rotate images',
            remove_all_images: 'Remove all images',
            remove_image: 'Remove image',
            dismiss_warning: 'Dismiss warning',
            duplicate_upload_confirmation: '“{filename}” is already in your upload. Select OK to upload it again, or Cancel to skip it.',
            tile_images: 'Tile images: {image_count}',
            delete_tiles_confirmation: 'Do you really want to delete one tile image? | Do you really want to delete all {count} tile images?',
            min_20_tiles: 'Please select at least 20 tile images.',
            not_enough_tiles: 'You have already uploaded 1 image. | You have already uploaded {count} images.',
            fix_errors: 'Please fix the following errors:',
            wait_for_upload: 'Please wait until all images have been uploaded',
            upload_failed: 'The upload could not be completed. Please retry the affected images.',
            confirm_delete_single_tile: 'Do you really want to delete the single image?',
            delete_tiles_failed: 'Some images could not be deleted. Please try again.',
            no_material_or_size_selected: 'Please select your desired material and size.',
            preview_creation_failed: 'The uploader could not be started. Please reload the page and try again.',
            image_validation_required: 'Please select an image.',
            image_validation_file: 'The selected upload is not a valid file.',
            image_validation_read: 'We could not read this photo. Download it to your device and select it again.',
            image_validation_image: 'The selected file must be a valid image.',
            image_validation_mimes: 'The image must have a JPG, JPEG, JFIF, PNG, HEIC or WEBP file extension.',
            image_validation_mimetypes: 'The image must be a JPEG, JFIF, PNG, HEIC or WEBP file.',
            image_validation_max: 'The image may not be larger than 50 MB.',
            image_validation_dimensions: 'The image may not be wider or higher than 12,000 pixels.',
            product_can_be_changed_afterwards: 'The product can be changed afterwards.',
            custom_size_wanted: 'If you would like a custom size for your mosaic, let us know in the next step using the message field.',
            first_name: 'First name',
            last_name: 'Last name',
            email_address: 'Email address (required)',
            your_message: 'Your message',
            your_message_placeholder: 'Do you have any comments or wishes?',
            as_partial_mosaic: 'As partial mosaic?',
            submit: 'Submit',
            submit_failed: 'Your preview request could not be submitted. Please try again.',
            email_invalid: 'Please check your email address.',
            submitted_thanks: 'Many thanks for your preview request!',
            submitted_email_send: 'We have just sent you a confirmation email and will get back to you within the next 12 hours to present your personal photo or partial mosaic.',
            check_spam_info: '<p><b>Attention:</b> Please also check your spam folder.</p>\n' +
                             '<p>If you use GoogleMail, check your advertising folder as well.</p>',
            new_preview: 'New preview',
            partial_mosaic_label: 'Partial mosaic description',
            partial_mosaic_required: 'Please describe which area we should crop.',
            partial_mosaic_placeholder: 'Please describe briefly which part of the main picture you do not want us to display as a mosaic.'
        },
        de: {
            design_rotation_failed: 'Dieses Foto konnte nicht gedreht werden. Bitte versuche es erneut.',
            design_saving: 'Wird gespeichert…',
            design_eyebrow: 'Dein Fotomosaik',
            design_free: 'Kostenlos & unverbindlich',
            design_steps: 'Fortschritt deiner Vorschauanfrage',
            design_step_count: 'Schritt {step} von {total}',
            design_step_1: 'Hauptbild',
            design_step_2: 'Einzelbilder',
            design_step_3: 'Produkt',
            design_step_4: 'Deine Wünsche',
            design_step_5: 'Fertig',
            design_title_1: 'Welches Bild erzählt deine Geschichte?',
            design_title_2: 'Deine Erinnerungen kommen zusammen.',
            design_title_3: 'Dein Mosaik nimmt Form an.',
            design_title_4: 'Noch ein paar persönliche Details.',
            design_title_5: 'Deine Erinnerungen sind in guten Händen.',
            design_description_1: 'Wähle das Hauptmotiv, das später aus deinen vielen kleinen Fotos entsteht.',
            design_description_2: 'Füge die Einzelbilder hinzu, aus denen dein Mosaik entsteht. Jedes Foto ist ein Teil deiner Geschichte.',
            design_description_3: 'Wähle Material und Größe. Nach der Vorschau kannst du dich jederzeit noch anders entscheiden.',
            design_description_4: 'Erzähle uns von deinen Wünschen. Unser Designteam gestaltet deine persönliche Vorschau.',
            design_description_5: 'Vielen Dank! Ab jetzt übernimmt unser Designteam für dich.',
            design_loading: 'Deine Vorschau wird vorbereitet…',
            design_privacy: 'Deine Fotos bleiben privat und werden nach kurzer Zeit gelöscht.',
            design_main_tip_title: 'Ein Motiv. Deine ganze Geschichte.',
            design_main_tip: 'Ein Porträt, eine Landschaft oder dein Lieblingsmoment — wähle ein klares Bild mit einem gut erkennbaren Motiv.',
            design_file_types: 'JPG / JPEG, JFIF, PNG, HEIC / HEIF, WEBP · Bis zu 50 MB pro Foto',
            design_next_tiles: 'Weiter zu den Einzelbildern',
            design_next_product: 'Weiter zum Produkt',
            design_next_contact: 'Weiter zu deinen Wünschen',
            design_tiles_uploaded: 'Fotos hochgeladen',
            design_tiles_recommendation: 'Mindestens 20 · Ab 50 empfohlen',
            design_tiles_hint: 'Über das + oben kannst du weitere Fotos hinzufügen.',
            design_expand: 'Vollbild',
            design_material: 'Material',
            design_size: 'Größe',
            design_room_preview: 'Vorschau deines ausgewählten Bildes an einer Wand',
            design_rotate_left: '{filename} nach links drehen',
            design_rotate_right: '{filename} nach rechts drehen',
            design_remove_named: '{filename} entfernen',
            alert_no_header: 'Bitte wähle ein Hauptbild aus.',
            next: 'Weiter',
            back: 'Zurück',
            close: 'Schließen',
            select: 'Auswählen',
            step_1_title: '1. Hauptbild auswählen',
            step_2_title: '2. Einzelbilder auswählen',
            step_3_title: '3. Produkt',
            step_4_title: '4. Kontakt',
            step_5_title: '5. Fertig',
            preview_code: 'Dein Vorschaucode: {preview_code}',
            add_more_images: 'Weitere Einzelbilder können mit dem "+" in der oberen rechten Ecke hinzugefügt werden.',
            fullscreen: 'Einzelbilder im Vollbild anzeigen',
            exit_fullscreen: 'Vollbild schließen',
            rotate_images: 'Einzelbilder drehen',
            remove_all_images: 'Alle Bilder löschen',
            remove_image: 'Bild entfernen',
            dismiss_warning: 'Hinweis ausblenden',
            duplicate_upload_confirmation: '„{filename}“ befindet sich bereits im Upload. Wähle OK, um es erneut hochzuladen, oder Abbrechen, um es nicht hochzuladen.',
            tile_images: 'Einzelbilder: {image_count}',
            delete_tiles_confirmation: 'Möchtest du wirklich ein Einzelbild löschen? | Möchtest du wirklich alle {count} Einzelbilder löschen?',
            min_20_tiles: 'Bitte wähle mindestens 20 Einzelbilder aus.',
            not_enough_tiles: 'Du hast bereits ein Bild hochgeladen. | Du hast bereits {count} Bilder hochgeladen.',
            fix_errors: 'Bitte behebe die folgenden Fehler:',
            wait_for_upload: 'Bitte warte bis alle Bilder hochgeladen worden sind.',
            upload_failed: 'Der Upload konnte nicht abgeschlossen werden. Bitte versuche die betroffenen Bilder erneut hochzuladen.',
            confirm_delete_single_tile: 'Möchtest du das Einzelbild wirklich löschen?',
            delete_tiles_failed: 'Einige Bilder konnten nicht gelöscht werden. Bitte versuche es erneut.',
            no_material_or_size_selected: 'Bitte wähle ein Material und eine Größe aus.',
            preview_creation_failed: 'Der Uploader konnte nicht gestartet werden. Bitte lade die Seite neu und versuche es erneut.',
            image_validation_required: 'Bitte wähle ein Bild aus.',
            image_validation_file: 'Die ausgewählte Datei ist keine gültige Datei.',
            image_validation_read: 'Dieses Foto konnte nicht gelesen werden. Lade es auf dein Gerät herunter und wähle es erneut aus.',
            image_validation_image: 'Die ausgewählte Datei muss ein gültiges Bild sein.',
            image_validation_mimes: 'Das Bild muss die Dateiendung JPG, JPEG, JFIF, PNG, HEIC oder WEBP haben.',
            image_validation_mimetypes: 'Das Bild muss eine JPEG-, JFIF-, PNG-, HEIC- oder WEBP-Datei sein.',
            image_validation_max: 'Das Bild darf nicht größer als 50 MB sein.',
            image_validation_dimensions: 'Das Bild darf höchstens 12.000 Pixel breit und hoch sein.',
            product_can_be_changed_afterwards: 'Das Produkt kann auch nachträglich noch geändert werden.',
            custom_size_wanted: 'Eine individuelle Wunschgröße kannst du im nächsten Schritt in der Nachricht vermerken.',
            first_name: 'Vorname',
            last_name: 'Nachname',
            email_address: 'E-Mail-Adresse (Pflichtfeld)',
            your_message: 'Deine Nachricht',
            your_message_placeholder: 'Hast du noch eine Anmerkung oder einen Wunsch? Solltest du eine individuelle Mosaikgröße wünschen, schreibe uns um welche Größe es sich handelt.',
            as_partial_mosaic: 'Als Teilmosaik?',
            submit: 'Gratis-Vorschau anfragen',
            submit_failed: 'Deine Vorschauanfrage konnte nicht abgesendet werden. Bitte versuche es erneut.',
            email_invalid: 'Bitte prüfe deine E-Mail Adresse.',
            submitted_thanks: 'Vielen Dank für Deine Vorschauanfrage!',
            submitted_email_send: 'Wir haben dir soeben eine E-Mail zur Bestätigung geschickt. Wir werden uns in den nächsten 12 Stunden bei dir melden und dir dein persönliches Foto- oder Teilmosaik präsentieren.',
            check_spam_info: '<p><b>Achtung:</b> Bitte überprüfe auch deinen Spam-Ordner.</p>\n',
            new_preview: 'Neue Vorschauanfrage',
            partial_mosaic_label: 'Teilmosaik Wunsch',
            partial_mosaic_required: 'Bitte beschreibe, welchen Bereich wir ausschneiden sollen.',
            partial_mosaic_placeholder: 'Bitte beschreibe kurz, welchen Teil des Hauptbildes du nicht als Mosaik dargestellt haben möchtest.'
        }
    }
})
